import {css} from '@emotion/react'
import {useStaticQuery, graphql} from 'gatsby'
import Img from 'gatsby-image'
import DynamicDownloadButton from './DynamicDownloadButton'

const translations = {
    en: {
        titleHighlight: 'Private AI Chat',
        description: 'Chat in secrecy, no tracking or logging.',
    },
    de: {
        titleHighlight: 'Privater KI-Chat',
        description: 'Chatte im Geheimen, kein Tracking oder Logging.',
    },
    nl: {
        titleHighlight: 'Privé AI Chat',
        description: 'Chat in het geheim, geen tracking of logging.',
    },
}

const Hero = ({languageCode = 'en', experimentId, os, browser}) => {
    const text = translations[languageCode] || translations.en

    const images = useStaticQuery(graphql`
        query PrivateAIHeroImages {
            desktopPhone: file(relativePath: {eq: "startpage/phones/app-model.png"}) {
                childImageSharp {
                    fluid(maxWidth: 628, quality: 100) {
                        ...GatsbyImageSharpFluid_noBase64
                    }
                }
            }
            spLogo: file(relativePath: {eq: "startpage/sp-logo-3.svg"}) {
                publicURL
            }
            logo: file(relativePath: {eq: "startpage/logo.svg"}) {
                publicURL
            }
        }
    `)

    return (
        <section
            className="sp-hero"
            css={css`
                width: 100%;
                background: linear-gradient(0deg, #29358F 20%, #000 100%);
                position: relative;
                overflow: hidden;
                box-sizing: border-box;
            `}
        >
            <div
                css={css`
                    width: 100%;
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    padding: 40px 20px 0px;
                    box-sizing: border-box;
                `}
            >
                {/* Top Section - Logo */}
                <div
                    className="top-section"
                    css={css`
                        display: flex;
                        justify-content: center;
                        position: relative;
                        z-index: 2;
                    `}
                >
                    <div
                        className="logo"
                        css={css`
                            display: flex;
                            align-items: center;
                        `}
                    >
                        <img
                            src={images.spLogo.publicURL}
                            alt="Startpage Logo"
                            css={css`
                                width: 42px;
                                height: 42px;
                                display: block;
                            `}
                        />
                    </div>
                </div>

                {/* Middle Section - Content */}
                <div
                    className="middle-section"
                    css={css`
                        flex: 1;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        text-align: center;
                        position: relative;
                        z-index: 2;
                        padding: 40px 20px 0px;
                        max-width: 100%;
                    `}
                >
                    <img
                        src={images.logo.publicURL}
                        alt="Startpage"
                        css={css`
                            height: 44px;
                            width: auto;
                            display: block;
                            margin-bottom: 20px;
                        `}
                    />
                    <div
                        css={css`
                            position: relative;
                            display: inline-block;
                        `}
                    >
                        <h1
                            css={css`
                                color: #f2f3fb;
                                text-align: center;
                                font-family: Alice;
                                font-size: 44px;
                                font-style: normal;
                                font-weight: 400;
                                line-height: 52px;
                                letter-spacing: 0.264px;
                                margin: 0 0 26px 0;

                                @media (max-width: 403px) {
                                    font-size: 32px;
                                    line-height: 40px;
                                }

                                .highlight {
                                    color: #38DDCC;
                                    font-family: 'Inter';
                                    font-style: normal;
                                    font-weight: 700;
                                    letter-spacing: 0.288px;
                                    line-height: 60px;
                                }
                            `}
                        >
                            <span className="highlight">{text.titleHighlight}</span>
                        </h1>
                    </div>

                    <p
                        css={css`
                            color: #EBECF7;
                            text-align: center;
                            font-family: 'Inter';
                            font-size: 16px;
                            font-style: normal;
                            font-weight: 400;
                            line-height: 24px;
                            letter-spacing: 0.048px;
                            margin: 0 0 32px 0;
                            max-width: 300px;

                            @media (max-width: 364px) {
                                font-size: 13px;
                                line-height: 20px;
                            }
                        `}
                    >
                        {text.description}
                    </p>
                    <div
                        css={css`
                            display: flex;
                            justify-content: center;
                            align-items: center;
                            width: 100%;
                        `}
                    >
                        <DynamicDownloadButton
                            ctaType="cta-hero"
                            experiment={experimentId}
                            os={os}
                            browser={browser}
                            languageCode={languageCode}
                        />
                    </div>

                    {/* Phone Mockup */}
                    <div
                        className="phone-mockup"
                        css={css`
                            max-width: 314px;
                            width: 100%;
                            margin-top: 40px;
                        `}
                    >
                        <Img
                            fluid={images.desktopPhone.childImageSharp.fluid}
                            alt="A smartphone screen of the Private AI chat app"
                            css={css`
                                width: 100%;
                                height: auto;
                                filter: drop-shadow(0 15px 40px rgba(0, 0, 0, 0.4));
                                image-rendering: -webkit-optimize-contrast;
                                image-rendering: crisp-edges;
                                image-rendering: pixelated;
                                /* Ensure crisp rendering on high-DPI displays */
                                @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
                                    image-rendering: auto;
                                }
                            `}
                        />
                    </div>
                </div>
            </div>
        </section>
    )
}

export default Hero
